import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StudentPicker from "./StudentPicker";
import { Plus, Trash2, Download, Mail, FileSpreadsheet, Copy, X } from "lucide-react";

const MAX_PROF = 20;
const MAX_STU = 12;

// V1.31.2 · Formatos DGT exigidos por la plantilla oficial CIR:
// · PROFESOR → "NOMBRE Y APELLIDOS" → NOMBRE + APELLIDO1 + APELLIDO2 (upper)
// · ALUMNO   → "APELLIDOS Y NOMBRE" → APELLIDO1 + APELLIDO2 + NOMBRE (upper)
const buildProfessorName = (t) => {
  const nombre = (t.name || "").trim();
  const a1 = (t.primer_apellido || "").trim();
  const a2 = (t.segundo_apellido || "").trim();
  const parts = [nombre, a1, a2].filter(Boolean);
  return (parts.length > 0 ? parts.join(" ") : (t.name || "")).toUpperCase();
};
const buildStudentName = (s) => {
  const nombre = (s.name || s.nombre || "").trim();
  const a1 = (s.primer_apellido || "").trim();
  const a2 = (s.segundo_apellido || "").trim();
  // Si no hay apellidos separados, devolvemos el `name` tal cual (retro-compat)
  if (!a1 && !a2) return nombre.toUpperCase();
  return [a1, a2, nombre].filter(Boolean).join(" ").toUpperCase();
};

export default function CirGeneratorPanel() {
  const [examDate, setExamDate] = useState("");
  const [cirCode, setCirCode] = useState("");
  const [examTime, setExamTime] = useState("");
  const [group, setGroup] = useState("");
  const [professors, setProfessors] = useState([]);
  const [pros, setPros] = useState([]);
  const [students, setStudents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    // V1.31.2 · Fuente única de profesores = `/teachers` (Profesorado UI).
    // Se descarta el legacy `/professors` para evitar duplicidad de fichas.
    api.get("/teachers").then(({ data }) => setPros(data || [])).catch(() => {});
    api.get("/students").then(({ data }) => setStudents(data || [])).catch(() => {});
    api.get("/vehicles").then(({ data }) => setVehicles(data || [])).catch(() => {});
  }, []);

  const addProfessor = () => {
    if (professors.length >= MAX_PROF) { toast.error(`Máximo ${MAX_PROF} profesores`); return; }
    setProfessors([...professors, {
      professor_name: "", professor_dni: "",
      license_class: "", seccion: "", dc: "", vehicle_plate: "",
      students: [],
    }]);
  };

  const updateProf = (idx, patch) => {
    const next = [...professors];
    next[idx] = { ...next[idx], ...patch };
    setProfessors(next);
  };

  const removeProf = (idx) => setProfessors(professors.filter((_, i) => i !== idx));

  const setProfFromDropdown = (idx, prof_id) => {
    const t = pros.find((x) => x.id === prof_id);
    if (!t) return;
    // V1.31.2 · Autofill desde `/teachers`. Nombre en formato "NOMBRE APELLIDOS"
    // (así lo exige la plantilla CIR — "NOMBRE Y APELLIDOS DEL PROFESOR").
    // Matrícula se resuelve desde el vehículo asignado; permanece editable.
    const veh = vehicles.find((v) => v.id === t.vehicle_id);
    updateProf(idx, {
      professor_name: buildProfessorName(t),
      professor_dni: (t.dni || "").toUpperCase(),
      license_class: (t.license_class || "B").toUpperCase(),
      vehicle_plate: veh ? (veh.plate || "").toUpperCase() : "",
    });
  };

  const addStudent = (pi) => {
    if (professors[pi].students.length >= MAX_STU) { toast.error(`Máx ${MAX_STU} alumnos por profesor`); return; }
    const next = [...professors];
    next[pi].students = [...next[pi].students, { dni: "", name: "" }];
    setProfessors(next);
  };

  const updateStu = (pi, si, patch) => {
    const next = [...professors];
    next[pi].students[si] = { ...next[pi].students[si], ...patch };
    setProfessors(next);
  };

  const removeStu = (pi, si) => {
    const next = [...professors];
    next[pi].students = next[pi].students.filter((_, i) => i !== si);
    setProfessors(next);
  };

  const setStuFromDropdown = (pi, si, sid) => {
    const s = students.find((x) => x.id === sid);
    if (!s) return;
    // V1.31.2 · Plantilla CIR exige "APELLIDOS Y NOMBRE" → APELLIDO1 APELLIDO2 NOMBRE.
    updateStu(pi, si, {
      dni: (s.dni || s.dni_nie || "").toUpperCase(),
      name: buildStudentName(s),
    });
  };

  const generate = async () => {
    if (!/^\d{8}$/.test(examDate)) { toast.error("Fecha debe ser DDMMAAAA"); return; }
    // V1.31.1 · Aceptamos "M 2174", "M2174", "M-2174" — backend normaliza a "M 2174"
    if (!/^[A-Z]\s?[\-_]?\d{3,5}$/i.test(cirCode.trim())) { toast.error("Código debe ser MXXXX (ej: M 2174)"); return; }
    if (professors.length === 0) { toast.error("Añade al menos un profesor"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/cir/generate", {
        exam_date: examDate,
        cir_code: cirCode.trim().toUpperCase(),
        exam_time: examTime,
        group_number: group,
        professors,
      });
      setLastResult(data);
      toast.success("CIR generado");
      // Descargar automáticamente el ODS
      const odsResp = await api.get(`/cir/${data.id}/download-ods`, { responseType: "blob" });
      const url = URL.createObjectURL(odsResp.data);
      const a = document.createElement("a"); a.href = url; a.download = data.ods_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const downloadEml = async () => {
    if (!lastResult) return;
    const resp = await api.get(`/cir/${lastResult.id}/download-eml`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a"); a.href = url; a.download = lastResult.eml_filename; a.click();
    URL.revokeObjectURL(url);
  };

  const openMailto = () => { if (lastResult?.mailto) window.location.href = lastResult.mailto; };
  const copyMailto = () => {
    if (!lastResult) return;
    navigator.clipboard.writeText(lastResult.mailto).then(() => toast.success("Enlace copiado"));
  };

  return (
    <div className="space-y-4" data-testid="cir-generator-panel">
      {/* Paso 1: cabecera */}
      <div className="card-surface p-3">
        <h3 className="font-semibold text-sm mb-1">1. Datos de la convocatoria</h3>
        <p className="text-[11px] text-neutral-500 mb-2">
          <strong>Hora</strong> y <strong>Grupo núm.</strong> son campos de la Jefatura Provincial de Tráfico —
          déjalos vacíos salvo indicación expresa. La <strong>Zona</strong> no se manipula desde aquí.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="text-xs font-semibold text-neutral-700 block">Fecha (DDMMAAAA)</label>
            <input type="text" value={examDate} onChange={(e) => setExamDate(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="17072026" data-testid="cir-exam-date" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 block">Código circulación</label>
            <input type="text" value={cirCode} onChange={(e) => setCirCode(e.target.value.toUpperCase().slice(0, 8))} placeholder="M 2174" data-testid="cir-code" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 block">Hora <span className="text-neutral-400 font-normal">(DGT — dejar vacío)</span></label>
            <input type="text" value={examTime} onChange={(e) => setExamTime(e.target.value)} placeholder="—" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 block">Grupo núm. <span className="text-neutral-400 font-normal">(DGT — dejar vacío)</span></label>
            <input type="text" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="—" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
      </div>

      {/* Paso 2: profesores */}
      <div className="card-surface p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">2. Profesores/Secciones ({professors.length}/{MAX_PROF})</h3>
          <button type="button" onClick={addProfessor} data-testid="cir-add-professor" className="btn-primary" disabled={professors.length >= MAX_PROF}>
            <Plus className="h-3.5 w-3.5" /> Añadir Profesor
          </button>
        </div>
        {professors.length === 0 && (
          <p className="text-sm text-neutral-500 italic">Añade al menos un profesor.</p>
        )}
        <div className="space-y-3">
          {professors.map((p, pi) => (
            <div key={pi} className="rounded-lg border-2 border-orange-200 bg-orange-50/30 p-3" data-testid={`cir-prof-${pi}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-sm">Profesor {pi + 1}</div>
                <button onClick={() => removeProf(pi)} className="text-red-700 hover:bg-red-50 rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                <div className="md:col-span-2">
                  <label className="text-[10px] font-semibold text-neutral-600 block">Profesor (autorrellena nombre, DNI, permiso y matrícula)</label>
                  <select value="" onChange={(e) => e.target.value && setProfFromDropdown(pi, e.target.value)} data-testid={`cir-prof-select-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="">— Seleccionar profesor de la BD —</option>
                    {pros.filter((pr) => pr.active !== false).map((pr) => <option key={pr.id} value={pr.id}>{`${buildProfessorName(pr)} · ${pr.dni || "sin DNI"}`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-neutral-600 block">DNI/NIE profesor</label>
                  <input type="text" value={p.professor_dni} onChange={(e) => updateProf(pi, { professor_dni: e.target.value.toUpperCase() })} data-testid={`cir-prof-dni-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-neutral-600 block">Nombre profesor (editable)</label>
                <input type="text" value={p.professor_name} onChange={(e) => updateProf(pi, { professor_name: e.target.value })} data-testid={`cir-prof-name-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                <div>
                  <label className="text-[10px] font-semibold text-neutral-600 block">Clase permiso</label>
                  <input type="text" value={p.license_class} onChange={(e) => updateProf(pi, { license_class: e.target.value })} placeholder="B" data-testid={`cir-prof-permiso-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-neutral-600 block">Sección</label>
                  <input type="text" value={p.seccion} onChange={(e) => updateProf(pi, { seccion: e.target.value })} placeholder="01" data-testid={`cir-prof-seccion-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-neutral-600 block">D.C.</label>
                  <input type="text" value={p.dc} onChange={(e) => updateProf(pi, { dc: e.target.value })} placeholder="03" data-testid={`cir-prof-dc-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-neutral-600 block">Matrícula</label>
                  <input type="text" value={p.vehicle_plate} onChange={(e) => updateProf(pi, { vehicle_plate: e.target.value.toUpperCase() })} placeholder="1508LJZ" data-testid={`cir-prof-plate-${pi}`} className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-8 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              {/* Alumnos */}
              <div className="border-t border-orange-200 pt-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-neutral-700">Alumnos ({p.students.length}/{MAX_STU})</div>
                  <button onClick={() => addStudent(pi)} data-testid={`cir-add-student-${pi}`} className="text-xs font-semibold text-orange-700 hover:bg-orange-100 rounded px-2 py-1 inline-flex items-center gap-1" disabled={p.students.length >= MAX_STU}>
                    <Plus className="h-3 w-3" /> Añadir Alumno
                  </button>
                </div>
                {p.students.map((s, si) => (
                  <div key={si} className="grid grid-cols-12 gap-1 mb-1" data-testid={`cir-stu-${pi}-${si}`}>
                    <div className="col-span-3">
                      <StudentPicker
                        value=""
                        onChange={(id) => id && setStuFromDropdown(pi, si, id)}
                        students={students.filter((st) => (st.name || "").trim())}
                        testId={`cir-stu-select-${pi}-${si}`}
                        placeholder="Buscar alumno…"
                        renderMeta={(st) => st.dni || "sin DNI"}
                        triggerClassName="h-7 rounded-lg text-[10px]"
                      />
                    </div>
                    <input type="text" value={s.dni} onChange={(e) => updateStu(pi, si, { dni: e.target.value.toUpperCase() })} placeholder="DNI/NIE" data-testid={`cir-stu-dni-${pi}-${si}`} className="col-span-3 rounded-lg border border-neutral-300 bg-white px-2 h-7 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                    <input type="text" value={s.name} onChange={(e) => updateStu(pi, si, { name: e.target.value.toUpperCase() })} placeholder="APELLIDOS Y NOMBRE" data-testid={`cir-stu-name-${pi}-${si}`} className="col-span-5 rounded-lg border border-neutral-300 bg-white px-2 h-7 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500" />
                    <button onClick={() => removeStu(pi, si)} className="col-span-1 text-red-700 hover:bg-red-50 rounded"><X className="h-3.5 w-3.5 mx-auto" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Generar */}
      <div className="flex items-center justify-end gap-2">
        <button onClick={generate} disabled={generating} data-testid="cir-generate-btn" className="btn-primary text-base">
          <FileSpreadsheet className="h-4 w-4" /> {generating ? "Generando…" : "Generar CIR"}
        </button>
      </div>

      {/* Resultado */}
      {lastResult && (
        <div className="card-surface p-3 border-2 border-emerald-300 bg-emerald-50/30" data-testid="cir-result">
          <h3 className="font-semibold text-sm mb-2">✅ CIR generado</h3>
          <div className="text-xs text-neutral-700 mb-2">
            📄 <strong>{lastResult.ods_filename}</strong> — descargado automáticamente.
          </div>
          <div className="text-xs text-neutral-700 mb-3">
            📎 <strong>{lastResult.eml_filename}</strong> — descarga opcional para abrir en Outlook con el adjunto ya incorporado.
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => api.get(`/cir/${lastResult.id}/download-ods`, { responseType: "blob" }).then(r => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = lastResult.ods_filename; a.click(); URL.revokeObjectURL(u); })} data-testid="cir-redownload-ods" className="btn-secondary">
              <Download className="h-3.5 w-3.5" /> Descargar ODS
            </button>
            <button onClick={downloadEml} data-testid="cir-download-eml" className="btn-secondary">
              <Mail className="h-3.5 w-3.5" /> Descargar .eml (Outlook)
            </button>
            <button onClick={openMailto} data-testid="cir-open-mailto" className="btn-primary">
              <Mail className="h-3.5 w-3.5" /> Abrir Outlook (mailto)
            </button>
            <button onClick={copyMailto} data-testid="cir-copy-mailto" className="btn-secondary">
              <Copy className="h-3.5 w-3.5" /> Copiar enlace mailto
            </button>
          </div>
          <p className="text-[10px] text-neutral-500 mt-3">
            💡 <strong>Recomendación</strong>: usa el archivo <code>.eml</code> — al abrirlo, Outlook mostrará ya el correo con destinatario, asunto, cuerpo y el ODS adjunto. Alternativa: usa el botón <em>mailto</em> y arrastra manualmente el ODS descargado.
          </p>
        </div>
      )}
    </div>
  );
}
